import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trophy, Plus, X, Minus, ExternalLink } from "lucide-react";

export function GameShowScoreboard({ show, isHost, characters, onChanged, onPopout }) {
  const [name, setName] = useState("");

  const addParticipant = async () => {
    if (!name.trim()) return toast.error("Give the contestant/team a name");
    try {
      await api.post(`/game-shows/${show.id}/participants`, { name, type: show.scoring_mode === "team" ? "team" : "individual" });
      setName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
    onChanged?.();
  };

  const removeParticipant = async (id) => {
    await api.delete(`/game-shows/${show.id}/participants/${id}`);
    onChanged?.();
  };

  const adjust = async (id, delta) => {
    await api.post(`/game-shows/${show.id}/participants/${id}/adjust-score`, { delta });
    onChanged?.();
  };

  const linkCharacter = async (id, characterId) => {
    await api.patch(`/game-shows/${show.id}/participants/${id}`, { character_id: characterId || null });
    onChanged?.();
  };

  const ranked = [...(show.participants || [])].sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-3" data-testid="gameshow-scoreboard">
      <h2 className="font-display text-xl flex items-center justify-between gap-2">
        <span className="flex items-center gap-2"><Trophy className="w-4 h-4 text-[#D4AF37]" /> Scoreboard</span>
        {onPopout && (
          <button data-testid="gameshow-scoreboard-popout-button" onClick={onPopout} title="Pop out into its own movable window" className="text-[#f1e0c5]/40 hover:text-[#D4AF37] transition-colors duration-150">
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </h2>
      {ranked.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No contestants yet {isHost ? "— add one below, or share the invite code." : "— wait for the host."}</p>}
      {ranked.map((p, i) => (
        <div key={p.id} data-testid={`gameshow-participant-${p.id}`} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`text-xs w-5 shrink-0 text-center ${i === 0 && p.score > 0 ? "text-[#D4AF37]" : "text-[#f1e0c5]/30"}`}>{i + 1}</span>
            <div className="min-w-0">
              <p className="text-sm truncate">{p.name} <span className="text-[10px] text-[#f1e0c5]/30 uppercase">{p.type === "team" ? "Team" : ""}</span></p>
              {isHost && (
                <Select value={p.character_id || ""} onValueChange={(v) => linkCharacter(p.id, v)}>
                  <SelectTrigger data-testid={`gameshow-link-character-${p.id}`} className="bg-black/30 border-white/10 h-6 text-[10px] mt-0.5 w-40"><SelectValue placeholder="Link a character…" /></SelectTrigger>
                  <SelectContent>
                    {(characters || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="font-display text-lg w-12 text-right" data-testid={`gameshow-score-${p.id}`}>{p.score || 0}</span>
            {isHost && (
              <>
                <button data-testid={`gameshow-score-minus-${p.id}`} onClick={() => adjust(p.id, -10)} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><Minus className="w-3.5 h-3.5" /></button>
                <button data-testid={`gameshow-score-plus-${p.id}`} onClick={() => adjust(p.id, 10)} className="text-[#f1e0c5]/40 hover:text-[#4d7c0f]"><Plus className="w-3.5 h-3.5" /></button>
                <button data-testid={`gameshow-remove-participant-${p.id}`} onClick={() => removeParticipant(p.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>
      ))}
      {isHost && (
        <div className="flex gap-1.5 pt-2 border-t border-white/10">
          <Input data-testid="gameshow-add-participant-name" placeholder={show.scoring_mode === "team" ? "Team name" : "Contestant name"} value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10 h-9 text-sm flex-1" />
          <Button data-testid="gameshow-add-participant-button" size="sm" className="h-9 rounded-full bg-[#3F9142] hover:bg-[#357a39]" onClick={addParticipant}>Add</Button>
        </div>
      )}
    </div>
  );
}
